import { useState } from "react";
import { AISummary } from "./AISummary";
import { CollapsiblePanel } from "./CollapsiblePanel";
import { PhotoAnalysisSection } from "./PhotoAnalysisSection";
import { VideoAnalysisSection } from "./VideoAnalysisSection";
import { SupplementWorkspaceButton } from "./SupplementWorkspaceButton";
import { AIQuickActions } from "./AIQuickActions";
import { SmartRecommendations } from "./SmartRecommendations";
import { JobProgressTracker } from "./JobProgressTracker";
import { ScheduleAppointmentDialog } from "./ScheduleAppointmentDialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Calendar } from "lucide-react";
interface OverviewTabProps {
  job: any;
  photos: any[];
  measurements: any;
  notes: any[];
  tasks: any[];
  documents: any[];
  jobTrades?: any[];
  onAddNote: () => void;
  onAddTask: () => void;
  onTabChange: (tab: string) => void;
}
export const OverviewTab = ({
  job,
  photos,
  measurements,
  notes,
  tasks,
  documents,
  jobTrades,
  onAddNote,
  onAddTask,
  onTabChange
}: OverviewTabProps) => {
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const totalSquares = measurements.roof?.reduce((sum: number, m: any) => sum + (m.total_squares || 0), 0) || 0;

  // Use a rounded whole number for display to avoid floating-point artifacts like 58.00000000001
  const displaySquares = Math.round(totalSquares);
  
  // Calculate total estimate from job_trades subtotals instead of hardcoded rate
  const totalEstimate = jobTrades?.reduce((sum, trade) => sum + (trade.subtotal || 0), 0) || 0;
  
  const photosCount = photos?.length || 0;
  const proposalStatus = "Not Started";
  
  // Insurance-specific calculations
  const isInsuranceJob = job.property_type === "Insurance";
  const daysSinceFiled = job.created_at 
    ? Math.floor((Date.now() - new Date(job.created_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0;
  
  return <div className="px-4 md:px-6 py-4 md:py-6 space-y-4 md:space-y-6">
      {/* Conditional Stat Cards based on Job Type */}
      {isInsuranceJob ? (
        // Insurance-specific stat cards
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 animate-fade-in">
          <Card className="stat-card p-6 bg-gradient-to-br from-primary/10 to-card border-primary/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-primary/80 uppercase tracking-wider">Claim Number</div>
              <div className="w-10 h-10 rounded-xl bg-primary/20 flex items-center justify-center premium-glow">
                <span className="text-primary text-xl">🏛️</span>
              </div>
            </div>
            <div className="text-2xl font-display font-bold text-primary">
              {job.claim_number || "Not Set"}
            </div>
            <div className="mt-2 text-xs text-primary/60">Insurance Claim</div>
          </Card>
          
          <Card className="stat-card p-6 bg-gradient-to-br from-secondary/10 to-card border-secondary/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-secondary/80 uppercase tracking-wider">Insurance Co.</div>
              <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center premium-glow">
                <span className="text-secondary text-xl">🏢</span>
              </div>
            </div>
            <div className="text-xl font-display font-bold text-secondary truncate">
              {job.insurance_company || "Not Specified"}
            </div>
            <div className="mt-2 text-xs text-secondary/60">Carrier</div>
          </Card>
          
          <Card className="stat-card p-6 bg-gradient-to-br from-accent/10 to-card border-accent/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-accent/80 uppercase tracking-wider">Adjuster</div>
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center premium-glow">
                <span className="text-accent text-xl">👤</span>
              </div>
            </div>
            <div className="text-xl font-display font-bold text-accent truncate">
              {job.adjuster_name || "Not Assigned"}
            </div>
            <div className="mt-2 text-xs text-accent/60">
              {job.adjuster_phone || "No contact"}
            </div>
          </Card>
          
          <Card className="stat-card p-6 bg-gradient-to-br from-warning/10 to-card border-warning/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-warning/80 uppercase tracking-wider">Days Open</div>
              <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center premium-glow">
                <span className="text-warning text-xl">📅</span>
              </div>
            </div>
            <div className="text-4xl font-display font-bold text-warning">{daysSinceFiled}</div>
            <div className="mt-2 text-xs text-warning/60">Since Filed</div>
          </Card>
        </div>
      ) : (
        // Default stat cards for Retail/Commercial
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 animate-fade-in">
          <Card className="stat-card p-6 bg-gradient-to-br from-success/10 to-card border-success/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-success/80 uppercase tracking-wider">Total Estimate</div>
              <div className="w-10 h-10 rounded-xl bg-success/20 flex items-center justify-center premium-glow">
                <span className="text-success text-xl">💰</span>
              </div>
            </div>
            <div className="text-4xl font-display font-bold text-success">
              {totalEstimate > 0 ? `$${totalEstimate.toLocaleString()}` : "—"}
            </div>
            <div className="mt-2 text-xs text-success/60">Revenue Target</div>
          </Card>
          
          <Card className="stat-card p-6 bg-gradient-to-br from-secondary/10 to-card border-secondary/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-secondary/80 uppercase tracking-wider">Roof Area</div>
              <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center premium-glow">
                <span className="text-secondary text-xl">📐</span>
              </div>
            </div>
            <div className="text-4xl font-display font-bold text-secondary">{displaySquares}</div>
            <div className="mt-2 text-xs text-secondary/60">Total Squares</div>
          </Card>
          
          <Card className="stat-card p-6 bg-gradient-to-br from-accent/10 to-card border-accent/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-accent/80 uppercase tracking-wider">Documentation</div>
              <div className="w-10 h-10 rounded-xl bg-accent/20 flex items-center justify-center premium-glow">
                <span className="text-accent text-xl">📸</span>
              </div>
            </div>
            <div className="text-4xl font-display font-bold text-accent">{photosCount}</div>
            <div className="mt-2 text-xs text-accent/60">Photos Captured</div>
          </Card>
          
          <Card className="stat-card p-6 bg-gradient-to-br from-warning/10 to-card border-warning/30">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-display font-semibold text-warning/80 uppercase tracking-wider">Proposal Status</div>
              <div className="w-10 h-10 rounded-xl bg-warning/20 flex items-center justify-center premium-glow">
                <span className="text-warning text-xl">📋</span>
              </div>
            </div>
            <div className="text-2xl font-display font-bold text-warning">{proposalStatus}</div>
            <div className="mt-2 text-xs text-warning/60">Awaiting Creation</div>
          </Card>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Two-column grid for AI features */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <AIQuickActions
              jobId={job.id}
              hasPhotos={photos.length > 0}
              hasMeasurements={totalSquares > 0}
              hasEstimate={documents.some(d => d.document_name?.toLowerCase().includes('estimate'))}
              onNavigateToTab={onTabChange}
            />
            
            <SmartRecommendations
              jobStatus={job.status}
              hasPhotos={photos.length > 0}
              hasMeasurements={totalSquares > 0}
              hasEstimate={documents.some(d => d.document_name?.toLowerCase().includes('estimate'))}
              hasTasks={tasks.length > 0}
              onNavigateToTab={onTabChange}
              onOpenForm={(formType) => {
                // Dispatch custom event for form opening
                window.dispatchEvent(new CustomEvent('bridgette-open-form', {
                  detail: { formType }
                }));
              }}
            />
          </div>

          {/* Job Progress Tracker */}
          <JobProgressTracker
            hasPhotos={photos.length > 0}
            hasMeasurements={totalSquares > 0}
            hasEstimate={documents.some(d => d.document_name?.toLowerCase().includes('estimate'))}
            hasProposal={false}
            isApproved={job.status === 'approved'}
          />
        </div>

        <div className="space-y-4">
          <PhotoAnalysisSection photos={photos} onViewAll={() => onTabChange('Photo Hub')} />

          {/* Insurance-specific Quick Access Widgets */}
          {isInsuranceJob && (
            <>
              <Card className="p-4 border-primary/20 bg-gradient-to-br from-purple-500/5 to-blue-500/5">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                      <span className="text-white text-sm">✨</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">AI Supplement Workspace</h4>
                      <p className="text-xs text-muted-foreground">Generate supplements from damage analysis</p>
                    </div>
                  </div>
                  <SupplementWorkspaceButton
                    jobId={job.id}
                    jobType={job.property_type}
                    hasEstimate={documents.some(d => d.document_name?.toLowerCase().includes('estimate') || d.document_name?.toLowerCase().includes('xactimate'))}
                    hasPhotos={photos.length > 0}
                    isProcessing={false}
                    photos={photos}
                    onNavigateToSupplements={() => onTabChange('Supplements')}
                    className="w-full"
                  />
                </div>
              </Card>

              <Card className="p-4 border-accent/20 bg-gradient-to-br from-orange-500/5 to-amber-500/5">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-600 to-amber-600 flex items-center justify-center">
                      <span className="text-white text-sm">📋</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Contingency Forms</h4>
                      <p className="text-xs text-muted-foreground">Generate insurance forms and documents</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('bridgette-open-form', {
                        detail: { formType: 'contingency_forms' }
                      }));
                    }}
                  >
                    Open Contingency Forms
                  </Button>
                </div>
              </Card>

              <Card className="p-4 border-secondary/20 bg-gradient-to-br from-cyan-500/5 to-blue-500/5">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-cyan-600 to-blue-600 flex items-center justify-center">
                      <span className="text-white text-sm">🔍</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-sm">Policy Scanner AI</h4>
                      <p className="text-xs text-muted-foreground">Analyze policy documents with AI</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="w-full"
                    onClick={() => {
                      window.dispatchEvent(new CustomEvent('bridgette-open-form', {
                        detail: { formType: 'policy_scanner' }
                      }));
                    }}
                  >
                    Open Policy Scanner
                  </Button>
                </div>
              </Card>
            </>
          )}

          <CollapsiblePanel title="Weather & Schedule">
            <p className="text-sm text-muted-foreground mb-4">
              Schedule appointments and manage calendar
            </p>
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full"
              onClick={() => setShowScheduleDialog(true)}
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule Appointment
            </Button>
          </CollapsiblePanel>

          <CollapsiblePanel title="Tasks">
            {tasks && tasks.length > 0 ? <div className="space-y-2">
                <div className="text-2xl font-bold">
                  {tasks.filter(t => t.status === 'pending').length}
                </div>
                <div className="text-sm text-muted-foreground">Pending tasks</div>
                <Button variant="link" size="sm" className="p-0 h-auto" onClick={() => onTabChange('Tasks')}>
                  View in Tasks Tab
                </Button>
              </div> : <div className="space-y-3">
                <p className="text-sm text-muted-foreground">No tasks yet</p>
                <Button variant="outline" size="sm" className="w-full" onClick={onAddTask}>
                  Create First Task
                </Button>
              </div>}
          </CollapsiblePanel>
        </div>
      </div>

      <ScheduleAppointmentDialog
        open={showScheduleDialog}
        onOpenChange={setShowScheduleDialog}
        jobId={job.id}
        jobAddress={job.property_address || undefined}
        ownerName={job.owner_name || undefined}
        onSuccess={() => {
          window.location.reload();
        }}
      />
    </div>;
};